import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';
import { emailService } from '@/services/email/emailService';
import type {
  UserProfile,
  UserProfileInsert,
  UserProfileUpdate,
  UserInvitation,
  UserInvitationInsert,
  Permission,
  UserRole,
  InviteUserData,
  UserWithPermissions,
} from '@/types/user.types';

export const userService = {
  // Fetch all user profiles
  async fetchUsers(): Promise<UserProfile[]> {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Fetch a single user profile
  async fetchUserById(id: string): Promise<UserProfile | null> {
    if (!id) {
      throw new Error('User ID is required');
    }

    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // Fetch current user profile
  async fetchCurrentUser(): Promise<UserProfile | null> {
    try {
      const { data: userData, error: authError } = await supabase.auth.getUser();
      if (authError || !userData?.user) return null;

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userData.user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        logError('Error fetching user profile:', error);
        // Handle infinite recursion error gracefully
        if (error.code === '42P17' || error.message?.includes('infinite recursion')) {
          return {
            id: userData.user.id,
            email: userData.user.email!,
            role: 'user',
            permissions: {},
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } as UserProfile;
        }
        throw error;
      }

      // If profile doesn't exist, create one
      if (!data) {
        const newProfile: UserProfileInsert = {
          email: userData.user.email!,
          role: 'user',
          first_name: userData.user.user_metadata?.first_name,
          last_name: userData.user.user_metadata?.last_name,
        };

        return await this.createUserProfile(userData.user.id, newProfile);
      }

      return data;
    } catch (error) {
      logError('Error in fetchCurrentUser:', error);
      throw error;
    }
  },

  // Create a user profile
  async createUserProfile(userId: string, profile: UserProfileInsert): Promise<UserProfile> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          id: userId,
          ...profile,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        logError('Error creating user profile:', error);
        throw error;
      }

      return data;
    } catch (error) {
      logError('Failed to create user profile:', error);
      throw error;
    }
  },

  // Update a user profile
  async updateUserProfile(id: string, update: UserProfileUpdate): Promise<UserProfile> {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(update)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete a user profile
  async deleteUserProfile(id: string): Promise<void> {
    const { error } = await supabase.from('user_profiles').delete().eq('id', id);

    if (error) throw error;
  },

  // Check if user has permission
  async hasPermission(permission: Permission): Promise<boolean> {
    const { data, error } = await supabase.rpc('has_permission', {
      user_id: (await supabase.auth.getUser()).data.user?.id,
      required_permission: permission,
    });

    if (error) throw error;
    return data || false;
  },

  // Get user permissions
  async getUserPermissions(userId?: string): Promise<Permission[]> {
    const targetUserId = userId || (await supabase.auth.getUser()).data.user?.id;
    if (!targetUserId) return [];

    const { data, error } = await supabase.rpc('get_user_permissions', {
      user_id: targetUserId,
    });

    if (error) throw error;
    return data?.map((row: { permission: Permission }) => row.permission) || [];
  },

  // Get user with permissions
  async fetchUserWithPermissions(userId: string): Promise<UserWithPermissions | null> {
    const user = await this.fetchUserById(userId);
    if (!user) return null;

    const permissions = await this.getUserPermissions(userId);
    return {
      ...user,
      permissions,
    };
  },

  // Invite a new user
  async inviteUser(inviteData: InviteUserData): Promise<UserInvitation> {
    // Generate invitation token
    const invitationToken = crypto.randomUUID();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7); // Expires in 7 days

    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('user_invitations')
      .insert({
        email: inviteData.email,
        role: inviteData.role,
        invited_by: userData.user.id,
        invitation_token: invitationToken,
        expires_at: expiresAt.toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    // Send invitation email
    await this.sendInvitationEmail(inviteData.email, invitationToken, inviteData, userData);

    return data;
  },

  // Send invitation email
  async sendInvitationEmail(
    email: string,
    token: string,
    inviteData: InviteUserData,
    inviterData: any
  ): Promise<void> {
    try {
      const inviteUrl = `${window.location.origin}/auth/accept-invite?token=${token}`;

      // Get inviter's profile
      const { data: inviterProfile } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', inviterData.user.id)
        .single();

      const inviterName = inviterProfile
        ? `${inviterProfile.first_name || ''} ${inviterProfile.last_name || ''}`.trim() ||
          inviterData.user.email
        : inviterData.user.email;

      // Get organization name if applicable
      let organizationName = 'Spherical Manager';
      if (inviteData.metadata?.organization_id) {
        const { data: org } = await supabase
          .from('organizations')
          .select('name')
          .eq('id', inviteData.metadata.organization_id)
          .single();
        if (org) organizationName = org.name;
      }

      await emailService.sendInvitation({
        email,
        inviterName,
        organizationName,
        role: inviteData.role,
        inviteUrl,
      });
    } catch (error) {
      logError('Failed to send invitation email', error);
      // Don't throw - invitation was created, just email failed
    }
  },

  // Fetch pending invitations
  async fetchPendingInvitations(): Promise<UserInvitation[]> {
    const { data, error } = await supabase
      .from('user_invitations')
      .select('*')
      .is('accepted_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Cancel invitation
  async cancelInvitation(id: string): Promise<void> {
    const { error } = await supabase.from('user_invitations').delete().eq('id', id);

    if (error) throw error;
  },

  // Accept invitation
  async acceptInvitation(
    token: string,
    userData: {
      password: string;
      first_name?: string;
      last_name?: string;
    }
  ): Promise<UserProfile> {
    // First, get the invitation
    const { data: invitation, error: inviteError } = await supabase
      .from('user_invitations')
      .select('*')
      .eq('invitation_token', token)
      .is('accepted_at', null)
      .gt('expires_at', new Date().toISOString())
      .single();

    if (inviteError || !invitation) {
      throw new Error('Invalid or expired invitation');
    }

    // Create the user account
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: invitation.email,
      password: userData.password,
      options: {
        data: {
          first_name: userData.first_name,
          last_name: userData.last_name,
        },
      },
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error('Failed to create user account');

    // Create user profile
    const profile = await this.createUserProfile(authData.user.id, {
      email: invitation.email,
      role: invitation.role,
      first_name: userData.first_name,
      last_name: userData.last_name,
    });

    // Mark invitation as accepted
    await supabase
      .from('user_invitations')
      .update({ accepted_at: new Date().toISOString() })
      .eq('id', invitation.id);

    return profile;
  },

  // Get role permissions
  async getRolePermissions(): Promise<Record<UserRole, Permission[]>> {
    const { data, error } = await supabase.from('role_permissions').select('role, permission');

    if (error) throw error;

    const rolePermissions: Record<UserRole, Permission[]> = {
      admin: [],
      manager: [],
      user: [],
      viewer: [],
    };

    data?.forEach((item) => {
      if (rolePermissions[item.role as UserRole]) {
        rolePermissions[item.role as UserRole].push(item.permission as Permission);
      }
    });

    return rolePermissions;
  },
};
